import { useState, useRef, useEffect } from 'react';
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  SkipBack,
  SkipForward,
  ChevronLeft,
  ChevronRight,
  Headphones,
  X,
  FastForward,
  Rewind,
  RotateCcw,
  RotateCw
} from 'lucide-react';
import { isIOSDevice, getIOSErrorMessage, prepareAudioForIOS, tryPlayAudioIOS } from '../utils/iosHelpers';
import { getStoredLanguage, normalizeLanguage } from '../utils/language';

const MediaPlayer = ({
  audioUrl,
  title,
  onClose,
  isVisible = true,
  onPrevious,
  onNext,
  hasPrevious = false,
  hasNext = false
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userInteracted, setUserInteracted] = useState(false);
  const audioRef = useRef(null);
  const [language, setLanguage] = useState(getStoredLanguage());
  const t = (tr, en, ru) => {
    if (language === 'EN') return en;
    if (language === 'RU') return ru || en || tr;
    return tr;
  };

  // iOS detection
  const isIOS = isIOSDevice();

  useEffect(() => {
    try {
      setLanguage(getStoredLanguage());
    } catch (_) {}
    const handler = (e) => {
      const next = normalizeLanguage(e?.detail?.language);
      setLanguage(next);
    };
    if (typeof window !== 'undefined') window.addEventListener('app-language-change', handler);
    return () => { if (typeof window !== 'undefined') window.removeEventListener('app-language-change', handler); };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleLoadStart = () => {
      setIsLoading(true);
      setError(null);
    };
    const handleCanPlay = () => setIsLoading(false);
    const handleEnded = () => setIsPlaying(false);
    const handleError = (e) => {
      const errorMessage = isIOS ? getIOSErrorMessage(e) : 'Ses dosyası yüklenemedi';
      setError(errorMessage);
      setIsLoading(false);
      setIsPlaying(false);
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [audioUrl, isIOS]);

  // iOS için audio element hazırlığı
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !isIOS) return;

    // iOS için audio element'i hazırla
    prepareAudioForIOS(audio);

    // iOS Range Request desteği için ek ayarlar
    audio.setAttribute('preload', 'metadata');
    audio.setAttribute('crossorigin', 'anonymous');
    
    // iOS'ta bazen currentTime'ı ayarlamak gerekebilir
    if (audio.currentTime === 0) {
      audio.currentTime = 0.01;
    }

    console.log('iOS: Audio element hazırlandı - Range Request desteği aktif');
  }, [audioUrl, isIOS]);

  // AudioUrl değiştiğinde player'ı resetle
  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setError(null);
    setIsLoading(false);
    setUserInteracted(false); // iOS için user interaction'ı resetle
  }, [audioUrl]);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (!audioUrl) {
      setError('Ses kaynağı bulunamadı');
      return;
    }

    // Loading state'de iken tıklamaları engelle
    if (isLoading) return;

    // iOS için ilk user interaction'ı kaydet
    if (!userInteracted) {
      setUserInteracted(true);
    }

    try {
      if (isPlaying) {
        // Pause işlemi - basit ve direkt
        audio.pause();
        setIsPlaying(false);
      } else {
        // Play işlemi - iOS için geliştirilmiş
        setIsLoading(true);
        setError(null);

        // iOS için kapsamlı hazırlık
        if (isIOS) {
          // iOS Safari için özel ayarlar
          audio.playsInline = true;
          audio.muted = false;
          audio.volume = 1;
          audio.preload = 'metadata';
          audio.crossOrigin = 'anonymous';

          // Webkit özellikleri
          audio.setAttribute('webkit-playsinline', 'true');
          audio.setAttribute('playsinline', 'true');
          audio.setAttribute('crossorigin', 'anonymous');

          // iOS Safari için ek ayarlar
          audio.style.webkitAppearance = 'none';
          audio.style.outline = 'none';

          console.log('iOS audio hazırlığı tamamlandı');
        }

        // Audio'yu yeniden yükle (iOS için önemli)
        audio.load();

        // iOS için özel yükleme ve oynatma
        if (isIOS) {
          try {
            // iOS için geliştirilmiş ses yükleme
            await new Promise((resolve, reject) => {
              const timeout = setTimeout(() => {
                cleanup();
                reject(new Error('iOS ses yükleme zaman aşımı'));
              }, 15000); // iOS için daha uzun timeout

              const onCanPlay = () => {
                console.log('iOS: Audio canplay event');
                cleanup();
                resolve();
              };

              const onCanPlayThrough = () => {
                console.log('iOS: Audio canplaythrough event');
                cleanup();
                resolve();
              };

              const onLoadedData = () => {
                console.log('iOS: Audio loadeddata event');
                cleanup();
                resolve();
              };

              const onError = (e) => {
                console.error('iOS: Audio error event:', e);
                cleanup();
                reject(new Error('iOS ses yükleme hatası: ' + (e.message || 'Bilinmeyen hata')));
              };

              const cleanup = () => {
                clearTimeout(timeout);
                audio.removeEventListener('canplay', onCanPlay);
                audio.removeEventListener('canplaythrough', onCanPlayThrough);
                audio.removeEventListener('loadeddata', onLoadedData);
                audio.removeEventListener('error', onError);
              };

              audio.addEventListener('canplay', onCanPlay);
              audio.addEventListener('canplaythrough', onCanPlayThrough);
              audio.addEventListener('loadeddata', onLoadedData);
              audio.addEventListener('error', onError);
            });

            // iOS için özel oynatma
            console.log('iOS: Ses oynatma deneniyor...');
            
            // iOS'ta bazen currentTime'ı ayarlamak gerekebilir
            if (audio.currentTime === 0) {
              audio.currentTime = 0.1; // Çok küçük bir değer
            }

            // iOS için tryPlayAudioIOS fonksiyonunu kullan
            await tryPlayAudioIOS(audio);
            setIsPlaying(true);
            setIsLoading(false);
            console.log('iOS: Ses başarıyla oynatılıyor');
            return;
          } catch (iosError) {
            console.error('iOS özel oynatma hatası:', iosError);
            // iOS özel hata durumunda normal yöntemi dene
          }
        }

        // Normal yükleme ve oynatma (iOS olmayan cihazlar veya iOS fallback)
        if (audio.readyState < 2) {
          await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
              cleanup();
              reject(new Error('Ses yükleme zaman aşımı'));
            }, 10000);

            const onCanPlay = () => {
              cleanup();
              resolve();
            };

            const onError = (e) => {
              cleanup();
              reject(new Error('Ses yükleme hatası: ' + (e.message || 'Bilinmeyen hata')));
            };

            const cleanup = () => {
              clearTimeout(timeout);
              audio.removeEventListener('canplay', onCanPlay);
              audio.removeEventListener('canplaythrough', onCanPlay);
              audio.removeEventListener('error', onError);
            };

            audio.addEventListener('canplay', onCanPlay);
            audio.addEventListener('canplaythrough', onCanPlay);
            audio.addEventListener('error', onError);
          });
        }

        // Ses oynatma
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          await playPromise;
        }
        setIsPlaying(true);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Ses oynatma hatası:', error);
      const errorMessage = isIOS ? getIOSErrorMessage(error) : 'Ses oynatılamadı';
      setError(errorMessage);
      setIsLoading(false);
      setIsPlaying(false);
    }
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isMuted) {
      audio.volume = volume;
      setIsMuted(false);
    } else {
      audio.volume = 0;
      setIsMuted(true);
    }
  };

  const skipTime = (seconds) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newTime = Math.max(0, Math.min(duration, currentTime + seconds));
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Navigation wrapper functions with scroll to top
  const handlePrevious = () => {
    // Sayfayı en üste kaydır
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Orijinal fonksiyonu çağır
    if (onPrevious) onPrevious();
  };

  const handleNext = () => {
    // Sayfayı en üste kaydır
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Orijinal fonksiyonu çağır
    if (onNext) onNext();
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40">
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="metadata"
        playsInline
        controls={false}
        style={{ display: 'none' }}
        crossOrigin="anonymous"
        webkit-playsinline="true"
      />



      {/* Error Message */}
      {error && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-200">
          <p className="text-sm text-red-600 text-center">{error}</p>
        </div>
      )}

      {/* Mobile Player */}
      <div className="lg:hidden">
        <div className="px-4 py-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3 min-w-0 flex-1">
              <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <Headphones className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="font-medium text-gray-900 text-sm truncate">{title}</h4>
                  <p className="text-xs text-gray-500">{t('Sesli Rehber', 'Audio Guide', 'Аудиогид')}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mb-3">
            <div
              className="w-full h-2 bg-gray-200 rounded-full cursor-pointer"
              onClick={handleSeek}
            >
              <div
                className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-150"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          {/* Navigation & Controls */}
          <div className="flex items-center justify-center space-x-4">
            <button
              onClick={handlePrevious}
              disabled={!hasPrevious}
              className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-30 disabled:cursor-not-allowed rounded-lg"
              aria-label={t('Önceki Bölüm', 'Previous section', 'Предыдущий раздел')}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <button
              onClick={() => skipTime(-10)}
              className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 transition-colors rounded-lg"
              disabled={isLoading}
              aria-label={t('10 saniye geri sar', 'Rewind 10s', 'Отмотать на 10с')}
            >
              <RotateCcw className="w-6 h-6" />
            </button>

            <button
              onClick={togglePlay}
              disabled={isLoading}
              className="w-14 h-14 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-full flex items-center justify-center hover:from-green-600 hover:to-emerald-600 transition-all duration-200 shadow-lg disabled:opacity-50"
              aria-label={isPlaying ? t('Duraklat', 'Pause', 'Пауза') : t('Oynat', 'Play', 'Воспроизвести')}
            >
              {isLoading ? (
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : isPlaying ? (
                <Pause className="w-7 h-7" />
              ) : (
                <Play className="w-7 h-7 ml-0.5" />
              )}
            </button>

            <button
              onClick={() => skipTime(10)}
              className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 transition-colors rounded-lg"
              disabled={isLoading}
              aria-label={t('10 saniye ileri sar', 'Forward 10s', 'Перемотать на 10с')}
            >
              <RotateCw className="w-6 h-6" />
            </button>

            <button
              onClick={handleNext}
              disabled={!hasNext}
              className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-30 disabled:cursor-not-allowed rounded-lg"
              aria-label={t('Sonraki Bölüm', 'Next section', 'Следующий раздел')}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Player */}
      <div className="hidden lg:block">
        <div className="px-6 py-4">
          <div className="flex items-center space-x-4">
            {/* Track Info */}
            <div className="flex items-center space-x-3 min-w-0 flex-1">
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <Headphones className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0">
                <h4 className="font-medium text-gray-900 truncate">{title}</h4>
                <p className="text-sm text-gray-500">{t('Sesli Rehber', 'Audio Guide', 'Аудиогид')}</p>
              </div>
            </div>

            {/* Navigation & Controls */}
            <div className="flex items-center space-x-3">
              <button
                onClick={handlePrevious}
                disabled={!hasPrevious}
                className="p-2 px-3 bg-primary-50 hover:bg-primary-100 text-primary-600 hover:text-primary-800 transition-colors disabled:opacity-30 disabled:cursor-not-allowed rounded-lg flex items-center gap-1 shadow-sm hover:shadow-md"
                title={t('Önceki Bölüm', 'Previous section', 'Предыдущий раздел')}
              >
                <ChevronLeft className="w-5 h-5" />
                <span className="text-sm font-medium">{t('Önceki', 'Previous', 'Предыдущий')}</span>
              </button>

              <button
                onClick={() => skipTime(-10)}
                className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 transition-colors rounded-lg"
                disabled={isLoading}
                title={t('10 saniye geri sar', 'Rewind 10s', 'Отмотать на 10с')}
              >
                <RotateCcw className="w-6 h-6" />
              </button>

              <button
                onClick={togglePlay}
                disabled={isLoading}
                className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-full flex items-center justify-center hover:from-green-600 hover:to-emerald-600 transition-all duration-200 shadow-lg disabled:opacity-50"
                title={isPlaying ? t('Duraklat', 'Pause', 'Пауза') : t('Oynat', 'Play', 'Воспроизвести')}
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : isPlaying ? (
                  <Pause className="w-5 h-5" />
                ) : (
                  <Play className="w-5 h-5 ml-0.5" />
                )}
              </button>

              <button
                onClick={() => skipTime(10)}
                className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 transition-colors rounded-lg"
                disabled={isLoading}
                title={t('10 saniye ileri sar', 'Forward 10s', 'Перемотать на 10с')}
              >
                <RotateCw className="w-6 h-6" />
              </button>

              <button
                onClick={handleNext}
                disabled={!hasNext}
                className="p-2 px-3 bg-primary-50 hover:bg-primary-100 text-primary-600 hover:text-primary-800 transition-colors disabled:opacity-30 disabled:cursor-not-allowed rounded-lg flex items-center gap-1 shadow-sm hover:shadow-md"
                title={t('Sonraki Bölüm', 'Next section', 'Следующий раздел')}
              >
                <span className="text-sm font-medium">{t('Sonraki', 'Next', 'Следующий')}</span>
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Progress */}
            <div className="flex-1 max-w-md">
              <div
                className="w-full h-2 bg-gray-200 rounded-full cursor-pointer"
                onClick={handleSeek}
              >
                <div
                  className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-150"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Volume */}
            <div className="flex items-center space-x-2">
              <button onClick={toggleMute} className="p-1 text-gray-600 hover:text-gray-800 transition-colors">
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                className="w-20 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            {/* Close */}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

    </div>
  );
};

export default MediaPlayer;
